import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { CloseMenu } from '@webapp/store/ui/action-creators';

// Types
import { RecordLabel } from '@common/types/record-label/record-label';
import { UPDATE_RECORD_LABEL } from '../../../store/record-labels/actions';

// Components
import NewMenu from '@components/new-menu';
import Flex from '@components/layout/flex';
import InputField from '@components/form/input';
import Button from '@components/actions/button';
import Typography from '@components/basics/typography';
import EditButton from '@components/basics/edit-button';
import { ErrorMessage } from '@components/basics/error-message';

// Controllers
import NewAvatarEditor from '@webapp/controllers/new-avatar-editor';
import UserAvatarInfo from '@components/basics/user-avatar-info';
import { UpdateRecordLabel } from '../../../store/record-labels/action-creators';

interface EditLabelMenuProps {
  className?: string;
  showErrors: boolean;
  isMobile?: boolean;
  label?: RecordLabel;
  closeMenu: () => CloseMenu;
  onSaveChanges: (data: RecordLabel) => UpdateRecordLabel;
}

const mapStateToProps = (state: AppState) => ({
  label: state.recordLabels.active,
  isMobile: state.ui.mobile,
  showErrors: !!state.ui.errors[UPDATE_RECORD_LABEL],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
  onSaveChanges: (data: RecordLabel) => dispatch(new UpdateRecordLabel(data)),
});

export const EditLabelMenu: React.FC<EditLabelMenuProps> = ({
  closeMenu,
  ...props
}) => {
  const {
    avatar: propsAvatar,
    name: propsName,
    updated_at: propsUpdated_at,
  } = props.label || {};

  const [name, setName] = React.useState(propsName || '');
  const [avatar, setAvatar] = React.useState(propsAvatar || undefined);
  const [lastUpdate, setLastUpdate] = React.useState(
    propsUpdated_at ? Number(new Date(propsUpdated_at)) : 0
  );

  React.useEffect(() => {
    setAvatar(propsAvatar || '');
    setName(propsName || '');
  }, [propsAvatar, propsName]);

  React.useEffect(() => {
    if (propsUpdated_at && Number(new Date(propsUpdated_at)) !== lastUpdate) {
      setLastUpdate(Number(new Date(propsUpdated_at)));
      closeMenu();
    }
  }, [propsUpdated_at, lastUpdate, closeMenu]);

  const validateFields = (label: RecordLabel) => {
    return !!label.id && !!label.name;
  };

  const fieldChange = (setter: any, value: any) => {
    setter(value);
  };

  const handleSaveChanges = () => {
    if (!props.label) {
      return;
    }

    const newLabel: RecordLabel = {
      ...props.label,
      name,
      avatar,
    };

    if (validateFields(newLabel)) {
      props.onSaveChanges(newLabel);
    }
  };

  return (
    <NewMenu
      className={props.className}
      button={<EditButton />}
      menuId="EDIT_LABEL_PROFILE"
      topHeader={
        props.isMobile && (
          <UserAvatarInfo
            src={propsAvatar}
            name={propsName || 'Default Label'}
          />
        )
      }
      header={
        !props.isMobile && (
          <UserAvatarInfo
            src={propsAvatar}
            name={propsName || 'Default Label'}
          />
        )
      }
      footer={
        <Flex gap={5}>
          <Button height={60} buttonType="secondary" onClick={closeMenu}>
            <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.DISCARD" />
          </Button>
          <Button height={60} buttonType="primary" onClick={handleSaveChanges}>
            <Translate id="ARTIST_PROFILE.MENU.AVATAR_EDITOR.SAVE" />
          </Button>
        </Flex>
      }
    >
      <Form autoComplete="off" onSubmit={(ev) => ev.preventDefault()}>
        <Content>
          <div style={{ width: '100%' }}>
            <Typography
              className="mb-30"
              type="analytics-nav"
              customFont="semi-bold"
            >
              <Translate id="LABEL_PROFILE.MENU.EDIT.TITLE" />
            </Typography>
            <NewAvatarEditor
              className="mb-20"
              url={avatar}
              onDrag={() => void 0}
              onChange={(url: string) => fieldChange(setAvatar, url)}
            />
            {props.showErrors && (
              <ErrorMessage
                className="mt-40 mb-10"
                errorId={UPDATE_RECORD_LABEL}
              />
            )}
            <InputField
              isGrey
              value={name}
              label={<Translate id="LABEL_PROFILE.NAME.LABEL" />}
              onChange={(value) => fieldChange(setName, value)}
            />
          </div>
        </Content>
      </Form>
    </NewMenu>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EditLabelMenu);

const Form = styled.form`
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;
